import { currencyTypes, securityAddOnsList } from "configs/settings";

export const filterUsers = (users, text) =>
  users.filter(
    (user) =>
      user.id.toString().includes(text) ||
      user.name.toLowerCase().includes(text.toLowerCase()) ||
      user.email.toLowerCase().includes(text.toLowerCase()) ||
      user.role.join(", ").toLowerCase().includes(text.toLowerCase()) ||
      user.createdAt.toLowerCase().includes(text.toLowerCase()) ||
      user.lastLogin.toLowerCase().includes(text.toLowerCase())
  );

export const sortUsers = (users, sortingColumn) =>
  sortingColumn
    ? [...users].sort((a, b) => {
      const field = sortingColumn.sortingField;
      const valueA = a[field];
      const valueB = b[field];
      if (valueA < valueB) return sortingColumn.isDescending ? 1 : -1;
      if (valueA > valueB) return sortingColumn.isDescending ? -1 : 1;
      return 0;
    })
    : users;

export const paginateItems = (items, currentPage, itemsPerPage) =>
  items.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

export const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const extractSharedDeploymentData = (apiResponse) => {
  if (!apiResponse || !Array.isArray(apiResponse.data)) return [];

  return apiResponse.data.map((item) => {
    const attrs = item.attributes || {};
    return {
      id: item.id,
      region: attrs.region?.trim() || "",
      domainName: attrs.domainName?.trim() || "",
      name: attrs.name?.trim() || "",
      console_url: attrs.console_url?.trim() || "",
      health: attrs.health ?? null,
      status: attrs.status || "",
      org_id: item.relationships.organization.data.id || null,
      creator_id: attrs.creator_id ?? ""
    };
  });
};

export const extractDedicatedDeploymentData = (apiResponse) => {
  if (!apiResponse || !Array.isArray(apiResponse.data)) return [];

  return apiResponse.data.map((item) => {
    const attrs = item.attributes || {};
    const relationships = item.relationships || {};

    return {
      id: item.id,
      region: attrs.region?.trim() || "",
      domainName: attrs.domain_name?.trim() || "", // match API key
      name: attrs.name?.trim() || "",
      console_url: attrs.console_url?.trim() || "", 
      health: attrs.health ?? null,
      status: attrs.status || "",
      subscriptionId: relationships.subscription?.data?.id || null,
      org_id: relationships.organization?.data?.id || null, // safe access
      payment_status: attrs.payment_status,
      version: attrs.version ?? "",
      creator_id: attrs.creator_id ?? "",
      created_at: attrs.created_at ?? ""
    };
  });
};


export const prepareSharedDeploymentPayload = ({
  region,
  name,
  organizationId,
}) => {
  return {
    data: {
      type: "shared-deployments",
      attributes: {
        region: region,
        name: name.trim(),
      },
      relationships: {
        organization: {
          data: {
            type: "organizations",
            id: organizationId,
          },
        },
      },
    },
  };
};

export const prepareDedicatedDeploymentPayload = ({
  region,
  name,
  plan,
  billingCycle,
  domainName,
  hostedZoneId,
  organizationId,
  subscriptionId,
  invoiceDetailsObject,
  selectedKcVersion,
  size,
  useAlb
}) => {
  return {
    data: {
      type: "dedicated-deployments",
      attributes: {
        region: region,
        domain_name: domainName.trim(),
        hosted_zone_id: hostedZoneId,
        name: name.trim(),
        plan: plan,
        billing_cycle: billingCycle,
        keycloak_version: selectedKcVersion,
        invoice_details: invoiceDetailsObject,
        size,
        ...(plan !== "develop" && {
          useAlb
        })
      },
      relationships: {
        organization: {
          data: {
            type: "organizations",
            id: organizationId,
          },
        },
        subscription: {
          data: {
            type: "subscriptions",
            id: subscriptionId,
          },
        },
      },
    },
  };
};

export const getDeploymentStatusType = (status) => {
  switch (status.toLowerCase()) {
    case "active":
      return "success";
    case "pending":
      return "pending";
    case "inactive":
      return "warning";
    case "error":
      return "error";
    case "decommissioned":
      return "error";
    case "destroying":
      return "pending";
    default:
      return "info"; // fallback
  }
};

export const getClusterStatusBadgeType = (status) =>{
  switch (status) {
    case "active":
      return "green";

    case "pending":
      return "blue";

    case "inactive":
      return "grey";

    case "error":
      return "red";

    case "decommissioned":
      return "red";

    case "destroying":
      return "grey";

    default:
      return "grey";
  }
};

export const getCurrencySymbol = (currency) => {
  if (!currency) return "";
  return currencyTypes[currency] ?? "";
};

export const getBasePath = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);
  return segments.length > 0 ? `/${segments[0]}` : "/";
};

export function mapAllowedValues(allowedValues = {}) {
  const disabledFeatureReason = "coming soon";

  return Object.values(allowedValues).map(entry => ({
    label: entry.label,
    value: entry.value,
    disabled: entry.disabled ?? false,
    disabledReason: entry.disabled ? disabledFeatureReason : undefined
  }));
}

export function mapKcVersionsAllowedValues(allowedValues = {}) {
  return Object.values(allowedValues).map(entry => ({
    value: entry.value,
    isLatest: entry.isLatest
  }));
}

export function getPaymentInformationLabel (field, value){
  // Handle null or undefined values
  if (value === null || value === undefined) {
    return "Not available";
  }
  
  switch(field){
    case "status":
      return value === "trial" ? "Free trial" : value === "paid" ? "Paid" : value === "canceled" ? "Subscription canceled" : "Waiting for payment";
    case "plan":
      return value === "develop" ? "Develop" : value === "scale" ? "Scale" : "Accelerate";
    case "billing_type":
      return value === "monthly" ? "Monthly" : "Yearly";
    case "size":
      return value === "small" ? "Small" : value === "medium" ? "Medium" : "Large";
    default: return "";
  }
}

export const handleDownload = (filename, lines) => {
  const fileContent = lines.join("\n");

  const blob = new Blob([fileContent], { type: "text/plain;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Open links in new tab
export function openInNewTab(url) {
  window.open(url, "_blank", "noopener,noreferrer");
}

export function extensionCategoryBagdeColor(category) {
  switch (category) {
    case "Authentication":
      return "blue";
    case "Utility":
      return "severity-low";
    case "Integration":
      return "severity-medium";
    case "Monitoring":
      return "severity-critical";
    default:
      return "blue";
  }
}

export const formatDateTime = (date) =>
  new Date(date).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }
);

export const formatDate = (date) =>
  new Date(date).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }
);

// Make first character of text uppercase
export function makeFirstCharacterUppercase(text){
  return text[0].toUpperCase() + text.slice(1);
}

export const getSecurityAddOnsMonthlyTotalPrice = (securityList) => {
    const total = securityAddOnsList
      .filter(addOn => securityList.includes(addOn.id))
      .reduce((total, addOn) => total + addOn.price, 0);

    return total;
};